import React from 'react';

import { Box, Divider, Grid, Typography, useTheme } from '@mui/material';

import LineItem from './LineItem';
import PerAcre from './PerAcre';
import {
  ArmiesAway,
  Book,
  Province,
  Spell,
} from '../../../@types/KingdomResponse';
import { ReactComponent as BarracksSVG } from '../../../svgs/building/building-military-barracks-svg-0.svg';
import { ReactComponent as TrainingSVG } from '../../../svgs/building/building-training-grounds-svg-0.svg';
import { ReactComponent as ArmiesSVG } from '../../../svgs/design/nav-armies-svg-1.svg';
import { ReactComponent as MilitarySVG } from '../../../svgs/design/nav-military-svg-1.svg';
import { ReactComponent as ScienceSVG } from '../../../svgs/design/nav-science-svg-1.svg';
import { ReactComponent as SpellsSVG } from '../../../svgs/design/nav-spells-svg-1.svg';
import { ReactComponent as SurveySVG } from '../../../svgs/design/nav-survey-svg-1.svg';
import { ReactComponent as ThroneSVG } from '../../../svgs/design/nav-throne-svg-1.svg';
import {
  getEffectFromSpell,
  hoursMinutesFromSeconds,
} from '../helpers/colHelpers';

const boxStyles = { display: 'flex', alignItems: 'center' };

const getSpanWithPct = (
  val: number | string | undefined,
  val2?: number | string | undefined
) => {
  return (
    <>
      {val || val2 ? <span>{val ?? 0}%</span> : null}
      {val2 ? <span style={{ color: 'orange' }}> ({val2}%)</span> : null}
    </>
  );
};

const getBuildings = (p: Province) => {
  const arr = p.survey?.buildings.filter((x) => x.pctCurr > 0 || x.pctInc > 0);
  if (!arr) return null;

  return arr.map((item, i) => (
    <LineItem
      key={item.name}
      label={item.name}
      value={getSpanWithPct(item.pctCurr, item.pctInc)}
    />
  ));
};

const getArmyAway = (a: ArmiesAway) => {
  return (
    <Typography variant='body2' component={'span'}>
      Gen: {a.generals}, Elites: {a.elites}, Specs: {a.oSpecs}, Horses:{' '}
      {a.horses}, Land: {a.land}, Ambush: {a.ambush}, Return:{' '}
      {hoursMinutesFromSeconds(a.secondsRemaining)}
    </Typography>
  );
};

const getSpells = (spells: Spell[]) =>
  spells.length
    ? spells
        .map((spell: Spell) => {
          const effect = getEffectFromSpell(spell.name);
          return spell.ticks
            ? `${effect?.shortName ?? spell.name} (${spell.ticks})`
            : `${effect?.shortName ?? spell.name}`;
        })
        .join(', ')
    : 'None found';

const getArmiesAway = (p: Province) => {
  if (!p.som) return null;
  return (
    <>
      <Box style={boxStyles} sx={{ my: 1, ml: 1 }}>
        {p.som?.armiesAway.length ? (
          <Typography variant='body2' component={'span'}>
            {p.som?.armiesAway.reduce((a, b) => a + b.generals, 0)} Generals (
            {p.som?.armiesAway.reduce((a, b) => a + b.land, 0)}
            {' acres).'}
          </Typography>
        ) : (
          <Typography variant='body2' component={'span'}>
            None
          </Typography>
        )}
      </Box>
      {p.som?.armiesAway.map((a: ArmiesAway, i: number) => (
        <Box key={`${a.secondsRemaining}_${i}`} sx={{ mx: 1 }}>
          <Divider />
          {getArmyAway(a)}
        </Box>
      ))}
    </>
  );
};

const getGridItem = (
  header: string,
  value: string | null,
  svg?: JSX.Element
) => {
  return (
    <Grid item style={{ ...boxStyles }}>
      {!!svg && <Box sx={{ width: '30px' }}>{svg}</Box>}
      <Typography
        variant='subtitle2'
        component={'span'}
        sx={{ marginRight: 1 }}
      >
        {header}:
      </Typography>{' '}
      <Typography variant='body2' component={'span'}>
        {value}
      </Typography>
    </Grid>
  );
};

interface Props {
  province: Province | undefined;
}

const ProvinceView: React.FC<Props> = ({ province }) => {
  const theme = useTheme();
  const svgProps = {
    height: '24px',
    width: '24px',
    fill: `${theme.palette.primary.main}`,
    marginRight: '5px',
  };

  if (!province) return null;
  const p: Province = province;
  const hasArmiesAway = p.som && p.som.armiesAway.length > 0;
  if (!province) return null;
  return (
    <Box sx={{ pt: 1 }}>
      <Typography variant='h4' gutterBottom component='div' textAlign='center'>
        {p.name} ({p.title})
      </Typography>
      <Grid
        container
        spacing={1}
        columns={{ xs: 1, sm: 2, md: hasArmiesAway ? 6 : 5 }}
      >
        <Grid item xs={1}>
          <Typography
            variant='body1'
            sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
          >
            <ThroneSVG style={svgProps} />
            Overview
          </Typography>
          <LineItem label='Race' value={p.race} />
          <LineItem label='Personality' value={p.sot?.personality} />
          <LineItem label='MAP' value={p.sot?.map} />
          <LineItem label='NW' value={p.sot?.networth} />
          <LineItem label='Land' value={p.sot?.land} />
          <LineItem label='BE' value={`${p.sot?.be}%`} />
          <LineItem label='Wages' value={`${p.ma?.wages}%`} />
          <LineItem label='Draft' value={p.ma?.draftRate} />
          <LineItem
            label='Money'
            value={<PerAcre val={p.sot?.money} acres={p.land} />}
          />
          <LineItem
            label='Food'
            value={<PerAcre val={p.sot?.food} acres={p.land} />}
          />
          <LineItem
            label='Runes'
            value={<PerAcre val={p.sot?.runes} acres={p.land} />}
          />
          <LineItem label='TB' value={p.sot?.tb} />
          <LineItem label='Credits' value={p.ma?.credits} />
          <LineItem
            label='Peasants'
            value={<PerAcre val={p.sot?.peasants} acres={p.land} />}
          />
          <LineItem
            label='Thieves'
            value={
              <PerAcre
                val={p.sot?.thieves}
                acres={p.land}
                val2={p.sot?.stealth}
              />
            }
          />
          <LineItem
            label='Wizards'
            value={
              <PerAcre val={p.sot?.wizards} acres={p.land} val2={p.sot?.mana} />
            }
          />
        </Grid>
        <Grid item xs={1}>
          <Typography
            variant='body1'
            sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
          >
            <SurveySVG style={svgProps} /> Survey
          </Typography>
          {getBuildings(province)}
        </Grid>
        <Grid item xs={1}>
          <Typography
            variant='body1'
            sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
          >
            <ScienceSVG style={svgProps} /> Science
          </Typography>
          <LineItem label='Allocated' value={p.sos?.allocatedBooks} />
          <LineItem label='Unallocated' value={p.sos?.unallocatedBooks} />
          <Box sx={{ mt: 1 }}>
            {p.sos?.books.map(
              (b: Book) =>
                b.effect > 0 && (
                  <LineItem
                    key={b.type}
                    label={b.type}
                    value={`${b.effect}%`}
                  />
                )
            )}
          </Box>
        </Grid>
        <Grid item xs={1}>
          <Typography
            variant='body1'
            sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
          >
            <MilitarySVG style={svgProps} />
            Military
          </Typography>
          <LineItem label='OME' value={`${p.som?.ome ? p.som.ome : null}%`} />
          <LineItem label='DME' value={`${p.som?.dme ? p.som.dme : null}%`} />
          <LineItem label='Total Off Points' value={p.sot?.offPoints} />
          <LineItem label='Total Def Points' value={p.sot?.defPoints} />
          <LineItem label='Off Home' value={p.som?.offPointsHome} />
          <LineItem label='Def Home' value={p.som?.defPointsHome} />
          <LineItem label='DS Def' value={p.calcs?.defSpecsRaw} />
          <LineItem label='OS Mounted' value={p.calcs?.mountedOffSpecsRaw} />
          <LineItem
            label='Soldiers'
            value={<PerAcre val={p.sot?.soldiers} acres={p.land} />}
          />
          <LineItem
            label='OffSpecs'
            value={<PerAcre val={p.sot?.oSpecs} acres={p.land} />}
          />
          <LineItem
            label='DefSpecs'
            value={<PerAcre val={p.sot?.dSpecs} acres={p.land} />}
          />
          <LineItem
            label='Elites'
            value={<PerAcre val={p.sot?.elites} acres={p.land} />}
          />
          <LineItem
            label='Horses'
            value={<PerAcre val={p.sot?.horses} acres={p.land} />}
          />
          <LineItem
            label='Prisoners'
            value={<PerAcre val={p.sot?.prisoners} acres={p.land} />}
          />
        </Grid>
        <Grid item xs={1}>
          <Typography
            variant='body1'
            sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
          >
            <BarracksSVG style={svgProps} /> Army Home
          </Typography>
          <LineItem label='Generals' value={p.som?.standingArmy.generals} />
          <LineItem label='Soldiers' value={p.som?.standingArmy.solds} />
          <LineItem label='OffSpecs' value={p.som?.standingArmy.oSpecs} />
          <LineItem label='Elites' value={p.som?.standingArmy.elites} />
          <LineItem label='Horses' value={p.som?.standingArmy.horses} />
          <Typography
            variant='body1'
            sx={{ display: 'flex', alignItems: 'center', my: 1 }}
          >
            <TrainingSVG style={svgProps} /> Training
          </Typography>
          <LineItem label='DefSpecs' value={p.som?.training.dSpecs} />
          <LineItem label='OffSpecs' value={p.som?.training.oSpecs} />
          <LineItem label='Elites' value={p.som?.training.elites} />
          <LineItem label='Thieves' value={p.som?.training.thieves} />
        </Grid>
        {hasArmiesAway && (
          <Grid item xs={1}>
            <Typography
              variant='body1'
              sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
            >
              <ArmiesSVG style={svgProps} /> Armies Away:
            </Typography>
            {getArmiesAway(p)}
          </Grid>
        )}
      </Grid>
      <Grid container spacing={1} sx={{ my: 1 }}>
        {!!p.sot &&
          getGridItem(
            'Good Spells',
            getSpells(p.sot.goodSpells),
            <SpellsSVG style={svgProps} />
          )}
        {!!p.sot &&
          getGridItem(
            'Bad Spells',
            getSpells(p.sot.badSpells),
            <SpellsSVG style={svgProps} />
          )}
      </Grid>
    </Box>
  );
};
export default ProvinceView;
